import { useMemo } from 'react';

import { GbBreakdown, MeteringAIUsage, MeteringDataUsage, SapMeteringInfo } from './apiTypes';
import {
  BreakdownRow,
  CapacityProjection,
  CapacityStatus,
  CategoryMetric,
  EnvironmentUsageRow,
  SapUsageDashboardData
} from './types';
import { formatCredits, formatNumber, ratioAgainstMax, toPercent } from './utils';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function gbRows({ processed, egressed, stored }: GbBreakdown): BreakdownRow[] {
  const all = [processed, egressed, stored];
  const row = (label: string, gb: number): BreakdownRow => ({
    label,
    value: `${formatNumber(gb, 0)} GB`,
    ratio: ratioAgainstMax(gb, all)
  });

  return [row('Processed', processed), row('Egressed', egressed), row('Stored', stored)];
}

/** Each count is optional: the backend adds them one by one, and a missing one just hides its row. */
function activityRows(ai: MeteringAIUsage): BreakdownRow[] {
  const row = (label: string, count?: number): BreakdownRow | undefined =>
    count === undefined ? undefined : { label, value: formatNumber(count, 0), ratio: 0 };

  return [
    row('Pipelines modified', ai.pipelinesModified),
    row('Gems generated or modified', ai.gemsModified),
    row('Documents generated or modified', ai.documentsGenerated),
    row('Datasets harmonized', ai.datasetsHarmonized),
    row('Lines of code created/modified', ai.linesOfCodeModified),
    row('Lines of documentation created/modified', ai.linesOfDocumentationModified)
  ].filter((row): row is BreakdownRow => row !== undefined);
}

function totalGb({ processed, egressed, stored }: GbBreakdown): number {
  return processed + egressed + stored;
}

/** Both cycle dates are required: without them the pace figures have nothing to describe. */
function buildProjection(info: SapMeteringInfo): CapacityProjection | undefined {
  const { capacity } = info;
  if (!capacity.cycleStart || !capacity.cycleEnd) return undefined;

  const exhaustionDate = parseDate(capacity.projectedExhaustionDate);

  return {
    burnRatePerDay: capacity.burnRatePerDay,
    daysBeforeCycleEnds: capacity.daysRemainingInCycle,
    daysLeftAtCurrentPace: daysUntil(exhaustionDate),
    projectedCycleEndCredits: capacity.projectedCycleEndUsage,
    exhaustionDate
  };
}

/** The API types this as an unvalidated string, and date-fns throws on an Invalid Date. */
function parseDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** The API gives the exhaustion date, not the day count the card renders. */
function daysUntil(date?: Date): number | undefined {
  if (!date) return undefined;
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / MS_PER_DAY));
}

function determineCapacityStatus({
  consumed,
  allocated,
  projectedCycleEndCredits
}: {
  consumed: number;
  allocated: number;
  projectedCycleEndCredits?: number;
}): CapacityStatus {
  if (consumed >= allocated) return 'exhausted';
  if (projectedCycleEndCredits !== undefined && projectedCycleEndCredits > allocated) return 'runningOutFast';
  return 'onTrack';
}

function buildDataCategory(data: MeteringDataUsage): CategoryMetric {
  return {
    key: 'data',
    label: 'Data',
    credits: data.credits,
    caption: `${formatNumber(totalGb(data.gbBreakdown), 0)} GB processed, moved & stored`,
    sharePercent: data.shareOfUsage,
    breakdown: gbRows(data.gbBreakdown)
  };
}

function buildEnvironments(info: SapMeteringInfo): EnvironmentUsageRow[] {
  return info.environments.map((environment) => ({
    id: environment.executionPlaneID,
    name: environment.name || environment.controlPlaneURL || environment.executionPlaneID,
    tenantId: environment.tenantID || environment.controlPlaneURL,
    dataCredits: environment.data.credits,
    dataBreakdown: gbRows(environment.data.gbBreakdown)
  }));
}

function cycleElapsedPercentOf({ cycleStart, cycleEnd }: SapMeteringInfo['capacity']): number {
  if (!cycleStart || !cycleEnd) return 0;
  const start = new Date(cycleStart).getTime();
  const span = new Date(cycleEnd).getTime() - start;
  if (!Number.isFinite(span) || span <= 0) return 0;
  return toPercent(Date.now() - start, span);
}

export function useSapUsageDashboard(info: SapMeteringInfo): SapUsageDashboardData {
  return useMemo(() => {
    const { capacity, usageGroups } = info;
    const rates = info.rates;
    const projection = buildProjection(info);
    const environments = buildEnvironments(info);
    const environmentCredits = environments.reduce((sum, env) => sum + env.dataCredits, 0);
    const infrastructureRates = rates?.infrastructure;

    return {
      creditsConsumed: capacity.consumed,
      totalCreditsAllocated: capacity.allocated,
      usedPercent: toPercent(capacity.consumed, capacity.allocated),
      cycleElapsedPercent: cycleElapsedPercentOf(capacity),
      status: determineCapacityStatus({
        consumed: capacity.consumed,
        allocated: capacity.allocated,
        projectedCycleEndCredits: projection?.projectedCycleEndCredits
      }),
      projection,
      categories: [
        buildDataCategory(usageGroups.data),
        {
          key: 'ai',
          label: 'AI',
          credits: usageGroups.ai.credits,
          caption: 'Billed on AI activity across your account',
          sharePercent: usageGroups.ai.shareOfUsage,
          breakdown: activityRows(usageGroups.ai)
        },
        {
          key: 'infrastructure',
          label: 'Infrastructure',
          credits: usageGroups.infrastructure.credits,
          caption: 'Users, control plane & data planes',
          sharePercent: usageGroups.infrastructure.shareOfUsage,
          breakdown: []
        }
      ],
      environments,
      environmentsSummary: {
        totalCredits: capacity.consumed,
        environmentCredits,
        // Whatever the per-environment rows don't account for is billed at account level.
        accountLevelCredits: capacity.consumed - environmentCredits
      },
      seats: {
        totalSeats: info.totalUsers,
        activatedSeats: info.activatedUsers,
        includedUsers: info.includedUsers,
        additionalUsers: info.additionalUsers,
        creditsPerAdditionalUser: infrastructureRates?.perAdditionalUser
      },
      dataRates: rates?.data
        ? [
            { value: formatNumber(rates.data.perGbProcessed, 4), label: 'gb Processed' },
            { value: formatNumber(rates.data.perGbEgressed, 4), label: 'gb Egressed' },
            { value: formatNumber(rates.data.perGbStoredPerMonth, 4), label: 'gb Stored / Month' }
          ]
        : [],
      // The AI rate card is deliberately not exposed to end users, so `rates.ai` arrives omitted.
      aiRateNote: 'Based Directly on Consumption',
      infrastructureRates: infrastructureRates
        ? [
            { value: formatCredits(infrastructureRates.perAdditionalUser), label: 'cu / additional user' },
            {
              value: formatCredits(infrastructureRates.perDedicatedControlPlane),
              label: 'cu / dedicated control plane'
            },
            {
              value: formatCredits(infrastructureRates.perAdditionalExecutionPlane),
              label: `cu / additional data plane (first ${infrastructureRates.includedExecutionPlanes} included)`
            }
          ]
        : []
    };
  }, [info]);
}
